import { useEffect, useState } from "react";
import axios from "axios";
import { ShieldCheck, LogOut, Loader2, Inbox, ArrowLeft } from "lucide-react";
import { LogoMark } from "@/components/landing/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#0D0F12] border border-slate-700 text-slate-100 text-base sm:text-sm px-4 py-3 sm:py-2.5 min-h-[48px] sm:min-h-0 placeholder:text-slate-600 focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition-colors duration-200";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("defcoin_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [enquiries, setEnquiries] = useState(null);

  const load = async (tok) => {
    try {
      const { data } = await axios.get(`${API}/admin/enquiries`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setEnquiries(data.enquiries);
    } catch {
      localStorage.removeItem("defcoin_admin_token");
      setToken("");
      setEnquiries(null);
    }
  };

  useEffect(() => {
    if (token) load(token);
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("defcoin_admin_token", data.access_token);
      setToken(data.access_token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Access denied.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("defcoin_admin_token");
    setToken("");
    setEnquiries(null);
  };

  return (
    <div className="min-h-screen bg-[#0D0F12] text-slate-50" data-testid="admin-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-800 pb-5 mb-8">
          <div className="flex items-center gap-3">
            <LogoMark className="h-10 w-10" id="admin-logo" />
            <div>
              <p className="font-brand font-extrabold tracking-tight text-lg">DEFCOIN <span className="text-slate-400 font-semibold">Ltd.</span></p>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-amber-600">Enquiry Inbox // Restricted</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase text-slate-400 hover:text-amber-600 transition-colors" data-testid="admin-back-link">
              <ArrowLeft className="w-4 h-4" /> Site
            </a>
            {token && (
              <button onClick={logout} className="flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase text-slate-400 hover:text-amber-600 border border-slate-700 hover:border-amber-600 px-4 py-2 transition-colors" data-testid="admin-logout-btn">
                <LogOut className="w-4 h-4" /> Logout
              </button>
            )}
          </div>
        </div>

        {!token ? (
          <form onSubmit={login} className="max-w-sm mx-auto mt-10 sm:mt-24 border border-slate-800 bg-[#14171A] p-6 sm:p-8 space-y-5" data-testid="admin-login-form">
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-amber-600">// Authorized Personnel</p>
            <h1 className="font-display font-bold uppercase tracking-tight text-xl">Admin Access</h1>
            <div>
              <label htmlFor="ad-email" className="block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">Email</label>
              <input id="ad-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="admin-email-input" />
            </div>
            <div>
              <label htmlFor="ad-pass" className="block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">Password</label>
              <input id="ad-pass" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="admin-password-input" />
            </div>
            {error && <p className="font-mono text-xs text-red-500" data-testid="admin-login-error">{error}</p>}
            <button type="submit" disabled={loading} className="w-full flex items-center justify-center gap-3 min-h-[48px] bg-blue-600 hover:bg-[#55695A] disabled:opacity-60 text-white font-mono text-[11px] tracking-[0.15em] uppercase px-6 py-3 border border-blue-600 hover:border-amber-600 transition-colors" data-testid="admin-login-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              {loading ? "Verifying..." : "Authenticate"}
            </button>
          </form>
        ) : (
          <div data-testid="admin-inbox">
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <Inbox className="w-4 h-4 text-amber-600" />
              <h1 className="font-display font-bold uppercase tracking-tight text-lg sm:text-xl">Defence Desk Submissions</h1>
              <span className="font-mono text-[11px] text-slate-500 tracking-[0.2em]" data-testid="admin-inbox-count">
                {enquiries ? `${enquiries.length} RECORDS` : "LOADING..."}
              </span>
            </div>
            {enquiries === null ? (
              <div className="flex items-center gap-3 text-slate-400 font-mono text-xs py-16 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" /> LOADING RECORDS...
              </div>
            ) : enquiries.length === 0 ? (
              <p className="text-slate-400 text-sm py-16 text-center font-mono" data-testid="admin-inbox-empty">No enquiries logged yet.</p>
            ) : (
              <div className="border border-slate-800 divide-y divide-slate-800">
                {enquiries.map((q) => (
                  <article key={q.id} className="bg-[#14171A] p-5 hover:bg-[#171B20] transition-colors" data-testid={`enquiry-${q.id}`}>
                    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                      <p className="text-sm text-slate-50 font-medium">
                        {q.name} <span className="text-slate-500">— {q.organization}</span>
                      </p>
                      <p className="font-mono text-[10px] tracking-[0.15em] text-slate-500 uppercase">{q.created_at}</p>
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 mb-2">
                      <a href={`mailto:${q.email}`} className="font-mono text-[11px] text-amber-600 hover:underline">{q.email}</a>
                      <span className="font-mono text-[11px] text-blue-500 uppercase tracking-[0.15em]">{q.subject}</span>
                    </div>
                    <p className="text-sm text-slate-400 leading-relaxed whitespace-pre-line">{q.message}</p>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
